import { TextInput } from "@/components";
import { ValidationPatterns } from "@/utils/form-validation";
import InputErrorMessage from "@/components/input-error-message";
import { Box, Button, Drawer, Grid, IconButton, Typography } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { useGetAllClientsQuery } from "@/services";
import { MultiCheckbox } from "@/components";
import HierarchicalCheckbox from "@/components/checkbox/hierarchical";
import { PAGE_OPTIONS, filterOptionsByAvailable, normalizeForStorage, collectAllKeys } from "@/roles/protection/pageOptions";
import { useAppDispatch, useAppSelector } from "@/store";
import { roles } from "@/roles";
import { useGetTenantAllowedPagesQuery, useSetPartnerAccessibleClientsMutation, useSetUserAllowedPagesMutation } from "@/graphql/page-access";
import { spOwnerPartnerApi } from "@/services/spowner/partner-api";
import { tokens } from "@/tokens";
import { CircularProgress } from "@mui/material";

type AddPartnerModalProps = {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  handleCreatePartner: (e: SPOwnerPartnerTypes) => void;
  btnLoader: boolean;
};

const AddPartnerModal: React.FC<AddPartnerModalProps> = ({
  show,
  setShow,
  handleCreatePartner,
  btnLoader = false,
}) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<SPOwnerPartnerTypes>();

  const user = useAppSelector((s) => s.auth.user);
  const { selectedTenant } = useAppSelector((s) => s.tenantManagement);
  const isTDAdminSP = user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "SERVICE_PROVIDER";
  const { data: clientsResp } = useGetAllClientsQuery({ pageNo: 1, pageSize: 100, searchKeyword: "", ...(isTDAdminSP && { tenantId: selectedTenant?._id }) });
  const clients = clientsResp?.data ?? [];
  const [selectedClientIds, setSelectedClientIds] = React.useState<string[]>([]);
  const { data: tenantPagesResp } = useGetTenantAllowedPagesQuery(
    isTDAdminSP ? { tenantId: selectedTenant?._id } : undefined
  );
  const [allowedPages, setAllowedPages] = React.useState<string[]>([]);
  const availablePageKeys = tenantPagesResp?.pages || [];
  const filteredOptions = filterOptionsByAvailable(PAGE_OPTIONS, availablePageKeys);
  const [setUserAllowedPages, { isLoading: savingPages }] = useSetUserAllowedPagesMutation();
  const [setPartnerClients, { isLoading: savingClients }] = useSetPartnerAccessibleClientsMutation();
  const dispatch = useAppDispatch();

  const isSubmitting = btnLoader || savingPages || savingClients;

  const formFields: Array<{
    name: keyof SPOwnerPartnerTypes;
    label: string;
    type: string;
    rules: object;
    placeholder: string;
  }> = [
    {
      name: "firstName",
      label: "First Name",
      type: "text",
      rules: { required: "First name is required" },
      placeholder: "Enter First name",
    },
    {
      name: "lastName",
      label: "Last Name",
      type: "text",
      rules: { required: "Last name is required" },
      placeholder: "Enter Last name",
    },
    {
      name: "email",
      label: "Email Address",
      type: "email",
      placeholder: "hi@example.com",
      rules: {
        required: "Email is required",
        pattern: {
          value: ValidationPatterns.email,
          message: "Invalid email address",
        },
      },
    },
  ];

  const onSubmit = handleSubmit(async (data) => {
    await handleCreatePartner(data);
    try {
      const resp = await dispatch(
        spOwnerPartnerApi.endpoints.getAllSpOwnerPartners.initiate({
          page: 1,
          limit: 10,
          searchKeyword: data.email,
          ...(isTDAdminSP && { tenantId: selectedTenant?._id }),
        })
      ).unwrap();
      const newPartner = resp?.data?.find((p) => p.email === data.email);
      if (newPartner?._id) {
        const validKeys = collectAllKeys(filteredOptions);
        const finalPages = normalizeForStorage((allowedPages || []).filter((k) => validKeys.has(k)), PAGE_OPTIONS);
        if (finalPages.length > 0) {
          await setUserAllowedPages({ userId: newPartner._id, pages: finalPages }).unwrap();
        }
        if (selectedClientIds.length > 0) {
          await setPartnerClients({ partnerId: newPartner._id, clientIds: selectedClientIds }).unwrap();
        }
      }
    } catch {}
  });

  return (
    <Drawer
      anchor="right"
      open={show}
      onClose={() => setShow(false)}
      PaperProps={{
        sx: {
          width: 480,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box sx={{
        px: 3,
        py: 2,
        borderBottom: `1px solid ${tokens.border.default}`,
        bgcolor: tokens.bg.surface,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <Box>
          <Typography sx={{ fontSize: tokens.fontSize.xl, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary }}>
            Add New Partner
          </Typography>
          <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.muted, mt: 0.25 }}>
            They'll receive an email invitation to join Riffs
          </Typography>
        </Box>
        <IconButton size="small" onClick={() => setShow(false)} sx={{ color: tokens.text.muted }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ flex: 1, overflow: 'auto', bgcolor: tokens.bg.muted, p: 3 }}>
        <Box component="form" onSubmit={onSubmit}>
          {/* Contact Info Card */}
          <Box sx={{ bgcolor: tokens.bg.surface, borderRadius: tokens.radius.lg, border: `1px solid ${tokens.border.default}`, p: 2.5, mb: 2.5 }}>
            <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Contact Info
            </Typography>
            <Grid container spacing={2}>
              {formFields.map((field) => (
                <Grid
                  item
                  xs={12}
                  sm={field.name === "email" ? 12 : 6}
                  key={field.name}
                >
                  <Controller
                    name={field.name}
                    control={control}
                    rules={field.rules}
                    render={({ field: { onChange, value } }) => (
                      <TextInput
                        label={field.label}
                        type={field.type}
                        placeholder={field.placeholder}
                        value={value as string}
                        handleChangeValue={onChange}
                        error={!!errors[field.name]}
                      />
                    )}
                  />
                  <InputErrorMessage message={errors[field.name]?.message} />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Assign Clients Card */}
          <Box sx={{ bgcolor: tokens.bg.surface, borderRadius: tokens.radius.lg, border: `1px solid ${tokens.border.default}`, p: 2.5, mb: 2.5 }}>
            <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Assign Clients
            </Typography>
            <MultiCheckbox
              label="Clients"
              checkboxes={clients.map((c) => ({ label: c.firstName }))}
              checked={clients.map((c) => selectedClientIds.includes(c._id))}
              handleParentChange={(e) => {
                const checked = e.target.checked;
                if (checked) setSelectedClientIds(clients.map((c) => c._id));
                else setSelectedClientIds([]);
              }}
              handleChildChange={(index) => () => {
                const id = clients[index]?._id;
                if (!id) return;
                setSelectedClientIds((prev) =>
                  prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                );
              }}
            />
          </Box>

          {/* Pages Card */}
          <Box sx={{ bgcolor: tokens.bg.surface, borderRadius: tokens.radius.lg, border: `1px solid ${tokens.border.default}`, p: 2.5 }}>
            <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Pages
            </Typography>
            <HierarchicalCheckbox
              options={filteredOptions}
              selectedKeys={allowedPages}
              onChange={setAllowedPages}
            />
            {filteredOptions.length === 0 && (
              <Typography sx={{ color: tokens.text.muted, fontSize: tokens.fontSize.base, mt: 0.5 }}>
                No tenant pages configured. Contact your administrator.
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{
        px: 3,
        py: 2,
        borderTop: `1px solid ${tokens.border.default}`,
        bgcolor: tokens.bg.surface,
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 1.5,
      }}>
        <Button
          variant="outlined"
          onClick={() => setShow(false)}
          disabled={isSubmitting}
          sx={{
            borderColor: tokens.border.default,
            color: tokens.text.body,
            borderRadius: tokens.radius.md,
            fontWeight: tokens.fontWeight.semibold,
            fontSize: tokens.fontSize.md,
            height: 38,
            px: 2.5,
            '&:hover': { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} sx={{ color: tokens.bg.surface }} /> : undefined}
          sx={{
            bgcolor: tokens.brand.primary,
            color: tokens.bg.surface,
            borderRadius: tokens.radius.md,
            fontWeight: tokens.fontWeight.semibold,
            fontSize: tokens.fontSize.md,
            height: 38,
            px: 2.5,
            boxShadow: 'none',
            '&:hover': { bgcolor: tokens.brand.primaryHover, boxShadow: 'none' },
          }}
        >
          Invite
        </Button>
      </Box>
    </Drawer>
  );
};

export default AddPartnerModal;
